import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { trackScoreComplete } from "../lib/ga";
import { CheckCircle2, Loader2 } from "lucide-react";

/**
 * Six-phase pseudo-progress that maps roughly to what the backend CRS v2.1
 * methodology actually does. Phases time-rotate based on elapsed seconds —
 * the LLM is one ~50s call, so we can't get true progress, but rotating
 * methodology phases gives founders something concrete to watch.
 */
export const ANALYSIS_PHASES = [
  { label: "Reading your documents",          startSec: 0   },
  { label: "Analysing financial maturity",    startSec: 30  },
  { label: "Scoring traction & team",         startSec: 70  },
  { label: "Computing CRS pillar weights",    startSec: 110 },
  { label: "Matching funder categories",      startSec: 150 },
  { label: "Finalising results",              startSec: 185 },
];

const POLL_INTERVAL_MS = 2500;
const POLL_INTERVAL_SUBMITTED_MS = 6000; // background job: keep polling gently while the page is open
const STATUS_ENDPOINT_TPL = (jobId) => `/ai/analyze/status/${jobId}`;

/**
 * useAnalysisRunner — shared analysis trigger + polling hook for all three
 * CRS analysis flows (initial onboarding, credit re-run, admin re-analyse).
 *
 * Contract:
 *   • POST `startEndpoint` (with optional `extraPayload`)
 *   • If response has `job_id`: poll `/ai/analyze/status/{job_id}` until status=done|failed
 *   • If response has `analysis` directly (sync legacy path) or `unchanged: true`
 *     (credit short-circuit): treat as immediate success — no polling
 *   • On done: call onDone(payload) where payload includes the analysis + any
 *     server-provided side-effect fields (credits_remaining, delta, etc.)
 *
 * Returns: { run, busy, phase, error, reset }
 */
/** "Your score moved from 58 → 63" the moment a re-run finishes. */
export function showScoreChangeToast(payload) {
  const prev = payload?.previous_score;
  const next = payload?.new_score;
  if (payload?.unchanged || typeof prev !== "number" || typeof next !== "number") return;
  const delta = next - prev;
  const arrow = `${prev} → ${next}`;
  if (delta > 0) {
    toast.success(`Your score moved from ${arrow}`, {
      description: `Up ${delta} point${delta === 1 ? "" : "s"} — nice work. Keep updating your documents to climb further.`,
      duration: 9000,
    });
  } else if (delta < 0) {
    toast.warning(`Your score moved from ${arrow}`, {
      description: `Down ${Math.abs(delta)} point${delta === -1 ? "" : "s"} — check the action checklist for what changed.`,
      duration: 9000,
    });
  } else {
    toast.info(`Your score stayed at ${next}`, {
      description: "No change this time — tick off checklist items and upload updated documents to move it.",
      duration: 8000,
    });
  }
}

export function useAnalysisRunner({ startEndpoint, onDone, onError }) {
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState(0);
  const [error, setError] = useState("");
  // `submitted` = the analysis is running as a background job; the founder is
  // shown a confirmation and may leave — we keep polling while mounted.
  const [submitted, setSubmitted] = useState(false);
  // `processing` = the backend is still reading uploaded documents (vision
  // pass in flight). Nothing was charged; the founder should retry later.
  const [processing, setProcessing] = useState(null);
  const tickerRef = useRef(null);
  const pollAbortRef = useRef({ cancelled: false });

  const stopTicker = useCallback(() => {
    if (tickerRef.current) {
      clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    stopTicker();
    pollAbortRef.current.cancelled = true;
    setBusy(false);
    setSubmitted(false);
    setProcessing(null);
    setPhase(0);
    setError("");
  }, [stopTicker]);

  useEffect(() => () => { stopTicker(); pollAbortRef.current.cancelled = true; }, [stopTicker]);

  // Poll a background job until done/failed. No upper bound: the backend
  // emails + notifies the founder, so this is only for the "still on the
  // page" case. Stale jobs are expired server-side.
  const pollJob = useCallback(async (jobId, finishOk, finishErr) => {
    const token = { cancelled: false };
    pollAbortRef.current = token;
    // Ticker keeps rotating phases while the background job runs so views
    // that stay on the page (admin re-run) show live progress until done.
    setSubmitted(true);
    setBusy(false);
    for (;;) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_SUBMITTED_MS));
      if (token.cancelled) return;
      let status;
      try {
        ({ data: status } = await api.get(STATUS_ENDPOINT_TPL(jobId)));
      } catch (pollErr) {
        if (pollErr?.response?.status === 404) { stopTicker(); setSubmitted(false); return; }
        continue;
      }
      if (token.cancelled) return;
      if (status.status === "done") {
        setSubmitted(false);
        finishOk({ ...status.result_meta, ...status, analysis: status.analysis });
        return;
      }
      if (status.status === "failed") {
        setSubmitted(false);
        finishErr(status.error || "Analysis failed. Please try again.");
        return;
      }
    }
  }, [stopTicker]);

  // Resume the confirmation screen + polling for a job started earlier
  // (page reload, re-login while the job is still running).
  const resume = useCallback((jobId) => {
    pollJob(jobId,
      (payload) => {
        setPhase(ANALYSIS_PHASES.length - 1); showScoreChangeToast(payload); onDone?.(payload);
        try { window.dispatchEvent(new Event("clinq:analysis-completed")); } catch (_e) { /* ignore */ }
      },
      (msg) => { setError(msg); onError?.(msg); });
  }, [pollJob, onDone, onError]);

  const run = useCallback(async (extraPayload = undefined) => {
    setBusy(true); setError(""); setPhase(0); setProcessing(null);
    const startedAt = Date.now();
    tickerRef.current = setInterval(() => {
      const elapsedSec = (Date.now() - startedAt) / 1000;
      let idx = 0;
      for (let i = 0; i < ANALYSIS_PHASES.length; i++) {
        if (elapsedSec >= ANALYSIS_PHASES[i].startSec) idx = i;
      }
      setPhase(idx);
    }, 1000);

    const finishOk = (payload) => {
      setPhase(ANALYSIS_PHASES.length - 1);
      stopTicker();
      setBusy(false);
      showScoreChangeToast(payload);
      trackScoreComplete(payload?.new_score ?? payload?.analysis?.readiness_score, { score_type: "full_analysis", previous_score: payload?.previous_score ?? undefined });
      onDone?.(payload);
      // Lets other mounted views (e.g. FounderDashboard) refetch the score.
      try { window.dispatchEvent(new Event("clinq:analysis-completed")); } catch (_e) { /* ignore */ }
    };
    const finishErr = (msg) => {
      stopTicker();
      setBusy(false);
      setError(msg);
      onError?.(msg);
    };

    try {
      const { data } = await api.post(startEndpoint, extraPayload);

      // Documents still being read (vision pass in flight). Free — nothing
      // was charged. Surface the "check back shortly" state and wake the
      // AppShell processing indicator so it shows the reading pill now.
      if (data?.status === "processing") {
        stopTicker();
        setBusy(false);
        setProcessing(data);
        try { window.dispatchEvent(new Event("clinq:processing-refresh")); } catch (_e) { /* ignore */ }
        toast.info(data.message || "We're still reading your documents — check back in about 5 minutes. No credit was used.",
          { duration: 10000 });
        return;
      }

      // Immediate-result paths (no background job needed).
      //
      // `unchanged: true` is the credit-rerun short-circuit: the founder's
      // documents are byte-for-byte identical to the last analysis so the
      // backend skips the (expensive) LLM call — but **still debits 1
      // credit**, because pressing "Re-run" is a billable action regardless
      // of whether the score actually moves. Without surfacing this clearly
      // the founder sees the button flicker back to idle and assumes nothing
      // happened. The toast now explicitly says "1 credit used".
      if (data?.unchanged) {
        toast.info(
          data.message ||
            "1 credit used — your documents haven't changed since the last run, so your Capital Readiness Score stays the same.",
          {
            description:
              "Upload a fresh pitch deck or financial model and try again to see a new score.",
            duration: 10000,
          }
        );
        finishOk(data);
        return;
      }
      if (data && data.analysis && !data.job_id) {
        finishOk(data);
        return;
      }
      const jobId = data?.job_id;
      if (!jobId) {
        finishErr("Analysis service did not return a job id. Please try again.");
        return;
      }

      await pollJob(jobId, finishOk, finishErr);
    } catch (e) {
      finishErr(e?.response?.data?.detail || e?.message || "Analysis failed. Try again.");
    }
  }, [startEndpoint, onDone, onError, stopTicker, pollJob]);

  return { run, resume, busy, submitted, running: busy || submitted, processing, phase, error, reset };
}

/**
 * <AnalysisProcessingCard> — shown when the backend is still reading the
 * founder's uploaded documents (vision pass in flight). Same visual family as
 * <AnalysisSubmittedCard>; nothing was charged.
 */
export function AnalysisProcessingCard({ info, testIdPrefix = "analysis", retrying = false }) {
  const n = info?.docs_processing || 0;
  const names = (info?.docs_processing_names || []).filter(Boolean);
  // Live "about N seconds left": expected duration minus how long the oldest
  // pending document has already been reading. Ticks every second.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const startedMs = info?.processing_started_at ? new Date(info.processing_started_at).getTime() : null;
  const expected = ANALYSIS_COUNTDOWN_SECS;
  const elapsed = startedMs ? Math.max(0, Math.floor((now - startedMs) / 1000)) : 0;
  const left = Math.max(0, expected - elapsed);
  const eta = retrying
    ? "Documents ready — re-running your analysis now…"
    : left > 0 ? `${mmss(left)} remaining` : "Finishing up — almost there…";
  return (
    <div data-testid={`${testIdPrefix}-processing-card`}
         className="rounded-2xl border border-[#0A8B85]/30 bg-[#0A8B85]/5 p-5">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#0A8B85]/15">
          <Loader2 className="h-4 w-4 animate-spin text-[#0A8B85]" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0A8B85]">Still reading your documents</p>
          <h3 className="mt-1 text-base font-extrabold text-neutral-900">
            We&rsquo;re still reading your documents, check back in about 5 minutes.
          </h3>
          <p className="mt-2 text-sm font-semibold text-[#0A8B85]" data-testid={`${testIdPrefix}-processing-eta`} aria-live="polite">
            {eta}
          </p>
          {!retrying && startedMs && (
            <div className="mt-2 h-1.5 w-full rounded-full bg-[#0A8B85]/15">
              <div className="h-1.5 rounded-full bg-[#0A8B85] transition-all"
                   style={{ width: `${Math.min(100, Math.round((Math.min(elapsed, expected) / expected) * 100))}%` }} />
            </div>
          )}
          <p className="mt-2 text-sm leading-relaxed text-neutral-700">
            {n > 0 && <>{n === 1 ? "1 document is" : `${n} documents are`} still being processed{names.length ? <> (<b>{names.join(", ")}</b>)</> : null}. </>}
            No credit was used — we&rsquo;ll re-run automatically the moment processing finishes.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * <AnalysisProgressPanel> — renders the 6-phase checklist with done / active /
 * pending states. Pass `busy` + `phase` from useAnalysisRunner. The panel
 * renders nothing when `busy` is false.
 */
export function AnalysisProgressPanel({ busy, phase, testIdPrefix = "analysis", countdown = false }) {
  if (!busy) return null;
  return (
    <div data-testid={`${testIdPrefix}-progress-panel`}
         className="mt-4 rounded-xl border border-[#0A8B85]/20 bg-[#0A8B85]/5 p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-[#0A8B85]">
          Analysis in progress
        </p>
        <span className="text-xs text-neutral-500">
          Usually done within 3½ minutes — the score refreshes when the job finishes
        </span>
      </div>
      <ol className="space-y-1.5">
        {ANALYSIS_PHASES.map((p, idx) => {
          const done = idx < phase;
          const active = idx === phase;
          const liCls =
            "flex items-center gap-2 text-sm " +
            (done ? "text-neutral-600"
             : active ? "font-semibold text-[#0A8B85]"
             : "text-neutral-400");
          let bullet;
          if (done) bullet = <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600"/>;
          else if (active) bullet = <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[#0A8B85]"/>;
          else bullet = <span className="h-4 w-4 shrink-0 rounded-full border border-neutral-300"/>;
          return (
            <li key={p.label}
                data-testid={`${testIdPrefix}-phase-${idx}`}
                className={liCls}>
              {bullet}
              <span>{p.label}</span>
            </li>
          );
        })}
      </ol>
      {countdown && <AnalysisCountdown testIdPrefix={testIdPrefix} />}
    </div>
  );
}

/**
 * <AnalysisProgressModal> — full-screen overlay variant of the phase stepper.
 * Blocks the page while analysis runs so founders can't accidentally close
 * the tab or navigate away. Shows an animated Loader2 spinner + the same
 * 6-phase vertical checklist as <AnalysisProgressPanel>, plus an elapsed-time
 * counter so ops can eyeball if a job is stuck.
 *
 * Renders nothing when `busy` is false — safe to always mount.
 */
// Countdown shown while documents are analysed — 3:30 (user-requested).
export const ANALYSIS_COUNTDOWN_SECS = 210;
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

function useCountdown(active, total = ANALYSIS_COUNTDOWN_SECS) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!active) { setElapsed(0); return undefined; }
    const start = Date.now();
    const id = setInterval(() => setElapsed(Math.round((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [active]);
  const left = Math.max(0, total - elapsed);
  return { elapsed, left, label: mmss(left), pct: Math.min(100, Math.round((elapsed / total) * 100)) };
}

/** Compact 3:30 countdown block used inside the "In progress" cards. */
export function AnalysisCountdown({ testIdPrefix = "analysis" }) {
  const { left, label, pct } = useCountdown(true);
  return (
    <div className="mt-4 rounded-xl border border-[#0A8B85]/20 bg-white/70 p-3" data-testid={`${testIdPrefix}-countdown`}>
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Estimated time remaining</p>
          <p className="text-3xl font-extrabold tabular-nums tracking-tight text-[#004B62]" data-testid={`${testIdPrefix}-countdown-time`}>{label}</p>
        </div>
        <p className="text-[11px] text-neutral-500">{left > 0 ? "Usually done within 3½ minutes" : "Almost there — finishing up…"}</p>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#E0F3F5]">
        <div className="h-full rounded-full bg-[#00828F] transition-[width] duration-1000 ease-linear" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function AnalysisProgressModal({ busy, phase, testIdPrefix = "analysis", note = null, title = "Analysing your documents" }) {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(null);
  useEffect(() => {
    if (!busy) { startRef.current = null; setElapsed(0); return; }
    startRef.current = Date.now();
    const id = setInterval(() => {
      setElapsed(Math.round((Date.now() - (startRef.current || Date.now())) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [busy]);
  if (!busy) return null;
  const doneCount = Math.min(phase, ANALYSIS_PHASES.length - 1);
  const phasePct = Math.round(((doneCount + 1) / ANALYSIS_PHASES.length) * 100);
  const left = Math.max(0, ANALYSIS_COUNTDOWN_SECS - elapsed);
  const timePct = Math.min(100, Math.round((elapsed / ANALYSIS_COUNTDOWN_SECS) * 100));
  // Bar follows the countdown, but never lags behind phase progress.
  const pct = Math.max(timePct, phasePct);
  return (
    <div
      data-testid={`${testIdPrefix}-progress-modal`}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testIdPrefix}-progress-modal-title`}
    >
      <div className="w-full max-w-md rounded-2xl border border-[#004B62]/20 bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center gap-3">
          <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#004B62]/10">
            <Loader2 className="h-6 w-6 animate-spin text-[#004B62]"/>
          </div>
          <div className="min-w-0 flex-1">
            <h2 id={`${testIdPrefix}-progress-modal-title`}
                className="text-base font-bold text-neutral-900">
              {title}
            </h2>
            <p className="text-xs text-neutral-500">
              {note || "Usually done within 3½ minutes · Please keep this tab open"}
            </p>
          </div>
          <div className="shrink-0 text-right">
            <div data-testid={`${testIdPrefix}-progress-countdown`}
                 className="text-2xl font-extrabold tabular-nums tracking-tight text-[#004B62]">
              {mmss(left)}
            </div>
            <div data-testid={`${testIdPrefix}-progress-elapsed`}
                 className="text-[10px] font-medium uppercase tracking-wider text-neutral-400 tabular-nums">
              {left > 0 ? "time remaining" : "finishing up…"}
            </div>
          </div>
        </div>

        <div className="mb-4 h-1.5 w-full overflow-hidden rounded-full bg-[#E0F3F5]">
          <div
            data-testid={`${testIdPrefix}-progress-bar`}
            className="h-full rounded-full bg-[#00828F] transition-[width] duration-1000 ease-linear"
            style={{ width: `${pct}%` }}
          />
        </div>

        <ol className="space-y-2">
          {ANALYSIS_PHASES.map((p, idx) => {
            const done = idx < phase;
            const active = idx === phase;
            const liCls =
              "flex items-center gap-2.5 text-sm " +
              (done ? "text-neutral-600"
               : active ? "font-semibold text-[#004B62]"
               : "text-neutral-400");
            let bullet;
            if (done) bullet = <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600"/>;
            else if (active) bullet = <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[#00828F]"/>;
            else bullet = <span className="h-4 w-4 shrink-0 rounded-full border border-neutral-300"/>;
            return (
              <li key={p.label}
                  data-testid={`${testIdPrefix}-modal-phase-${idx}`}
                  className={liCls}>
                {bullet}
                <span>{p.label}</span>
              </li>
            );
          })}
        </ol>

        <p className="mt-5 border-t border-neutral-100 pt-3 text-[11px] leading-relaxed text-neutral-400">
          We&apos;re reading your docs, benchmarking against sector peers, and
          drafting your Capital Readiness report. Stay put — closing the tab
          won&apos;t stop the analysis, but you&apos;ll miss the auto-scroll to
          your fresh score.
        </p>
      </div>
    </div>
  );
}

/**
 * Phase label for inline usage (e.g. button text while running).
 */
export const phaseLabel = (phase) =>
  ANALYSIS_PHASES[Math.max(0, Math.min(phase, ANALYSIS_PHASES.length - 1))].label;

/**
 * <AnalysisSubmittedCard> — confirmation shown the moment the analysis is
 * accepted as a background job. The founder can leave; we email them.
 */
export function AnalysisSubmittedCard({ email, venture = "business", testIdPrefix = "analysis", nextStep = "Step 5" }) {
  const isProject = venture === "project";
  const what = isProject ? "Project Readiness Score and Report" : "Capital Readiness Score and Report";
  const noun = isProject ? "project" : "business";
  return (
    <div data-testid={`${testIdPrefix}-submitted-card`}
         className="rounded-2xl border border-[#0A8B85]/30 bg-[#0A8B85]/5 p-6 sm:p-7">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#0A8B85]/15">
          <Loader2 className="h-4 w-4 animate-spin text-[#0A8B85]" />
        </span>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0A8B85]">In progress</p>
          <h3 className="mt-1 text-base font-extrabold text-neutral-900 md:text-lg">We&rsquo;re generating your {what} — we&rsquo;ll let you know when it&rsquo;s ready.</h3>
          <p className="mt-2 text-sm leading-relaxed text-neutral-700">
            We are reviewing your documents and answers and scoring your {noun} against what funders actually
            look for. You do not need to stay on this page — we will email
            {email ? <> <b>{email}</b></> : " you"} the moment your {what} are ready, and you will see a
            notification here the next time you log in.
          </p>
          <AnalysisCountdown testIdPrefix={testIdPrefix} />
          <p className="mt-3 text-xs text-neutral-500">
            {isProject
              ? <>If you keep this page open, we&rsquo;ll open your report automatically.</>
              : <>If you keep this page open, we&rsquo;ll move you to {nextStep} automatically.</>}
          </p>
        </div>
      </div>
    </div>
  );
}
